import { useRef, useMemo } from 'react'
import PeerList from './PeerList'
import { TextToggle } from '../ui-components/TextToggle'
import { useQuery } from '@tanstack/react-query'
import { fetchRollingPeerData, fetchTrailingPeerData } from '../../repository/MutualFundDetail'
import { useEffect, useState } from 'react'
import { PeerResponse, WaterfallSections, WaterfallValues } from '../../types/mutual-fund-detail.types'
import { convertUnitAndValue, opacityElementByAttribute, toPascalCaseWithSpaces } from '../../utils/utils'
import { EventNames, UNITS } from '../../types/misc.types'
import { PeerListSkeletonLoader } from '../skeletons/PeerListSkeleton'
import PeersSheet from './PeersSheet'
import useAlternateISIN from '../../hooks/useAlternateISIN'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'
import { trackEvent } from '../../utils/appBridge'
import { sortTenures } from '../../utils/mutualFundDetail'
import Remark from '../ui-components/Remark'

interface Fund {
  isin: string
  rank: number
  name: string
  returns: string
}

function sortFundsByRank(funds: Fund[]) {
  // Create a copy of the array to avoid modifying the original
  const sortedFunds = [...funds]

  // Sort the array based on the rank property
  sortedFunds?.sort((a, b) => {
    return (a?.rank || Infinity) - (b?.rank || Infinity)
  })

  return sortedFunds
}

export default function PeersSection({
  peersType,
  fundCategory
}: {
  peersType: 'ROLLING' | 'TRAILING'
  fundCategory: string
}) {
  const { data: metadata, directFundName, isin } = useMetaDataContext()
  const [peerData, setPeerData] = useState<Fund[]>([])
  const [currentToggleOption, setCurrentToggleOption] = useState('')
  const [toggleOptions, setToggleOptions] = useState<string[]>([])
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false)
  const peersMap = useRef<{ [x: string]: Fund[] }>({})

  const ScreenName = useMemo(() => {
    return peersType === 'ROLLING' ? `MF_L2_Rolling_Returns_Peer` : `MF_L2_Trailing_Returns_Peer`
  }, [peersType])

  const { alternateISIN, config } = useAlternateISIN(WaterfallSections.PEER)

  const { data, isLoading, isError } = useQuery<any, Error>({
    queryKey: [peersType === 'ROLLING' ? 'rollingPeersData' : 'trailingPeersData', alternateISIN, fundCategory],
    queryFn: () => {
      return peersType === 'ROLLING' ? fetchRollingPeerData(fundCategory) : fetchTrailingPeerData(fundCategory)
    },
    enabled: !!alternateISIN,
    refetchOnWindowFocus: false,
    retry: 1
  })

  useEffect(() => {
    if (data) {
      const dataCopy = data?.data as PeerResponse
      const tenures: string[] = []
      const mappedTenures =
        dataCopy?.peers?.map((datum) => {
          return datum?.tenure
        }) ?? []
      const sortedTenures = sortTenures(mappedTenures)
      sortedTenures?.forEach((datum) => {
        const tenure = `${convertUnitAndValue({
          value: datum?.value ?? 0,
          unit: datum?.period ?? UNITS.MONTH,
          format: {
            casing: 'PASCAL',
            type: 'SHORT'
          }
        })} ${peersType === 'ROLLING' ? 'Rolling' : 'Trailing'} Returns`
        tenures?.push(tenure)
        const uniqueTenures = [...new Set(tenures)]
        setToggleOptions(() => uniqueTenures)
        setCurrentToggleOption(() => uniqueTenures?.[0])
      })

      dataCopy?.peers?.forEach((datum) => {
        const tenure = `${convertUnitAndValue({
          value: datum?.tenure?.value ?? 0,
          unit: datum?.tenure?.period ?? UNITS.MONTH,
          format: {
            casing: 'PASCAL',
            type: 'SHORT'
          }
        })} ${peersType === 'ROLLING' ? 'Rolling' : 'Trailing'} Returns`

        if (peersMap && peersMap?.current)
          peersMap.current[`${tenure}`] = datum?.peersData?.map((datum) => {
            return {
              rank: datum?.rank,
              name: datum?.fundName,
              isin: datum?.isin,
              returns: convertUnitAndValue({
                value: datum?.fundReturns?.value ?? 0,
                unit: datum?.fundReturns?.unit ?? UNITS.PERCENTAGE
              })
            }
          })
      })
    }
  }, [data])

  useEffect(() => {
    if (peersMap && peersMap?.current) {
      const peerDataCopy = sortFundsByRank(peersMap?.current?.[currentToggleOption] ?? [])
      setPeerData(() => peerDataCopy)
    }
  }, [currentToggleOption])

  const getRank = () => {
    return peerData?.filter((peerDatum) => peerDatum?.isin === alternateISIN)?.[0]?.rank ?? 0
  }

  const openBottomSheet = () => {
    setIsSheetOpen(true)
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        eventId: 'View All',
        ScreenName,
        fundName: isin,
        screenType: 'MF_L2'
      }
    })
    opacityElementByAttribute('data-rsbs-backdrop', 'true', '1')
  }

  const closeBottomSheet = () => {
    setIsSheetOpen(false)
    opacityElementByAttribute('data-rsbs-backdrop', 'true', '0')
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        eventId: peersType === 'ROLLING' ? 'rolling_returns' : 'trailing_returns',
        ScreenName,
        fundName: isin,
        screenType: 'MF_L2'
      }
    })
  }, [])

  if (isLoading) return <PeerListSkeletonLoader />

  return (
    <div className='flex flex-col w-full'>
      {config === WaterfallValues.YES_DIRECT && (
        <div className='pb-6 w-full '>
          <Remark lineHeight={19} remarkClassName='py-3'>
            Showing Leaderboard for <span className='font-[500]'> Direct Growth Plans </span>
          </Remark>
        </div>
      )}
      <div className={`text-black text-[14px] font-[500] pb-1 `}>
        {peersType === 'ROLLING' ? 'Rolling' : 'Trailing'} Rank in {toPascalCaseWithSpaces(fundCategory)}
      </div>
      <div className={`flex gap-[4px] text-[#3D3D3D] pb-5 text-[14px] `}>
        {isError || peerData?.length <= 0 ? (
          <div className='text-[#8E9393] text-13 font-[700]'>Not Available</div>
        ) : (
          <div>Based on</div>
        )}
        <div>
          <TextToggle
            currentToggleOption={currentToggleOption}
            toggleOptions={toggleOptions}
            onToggleClick={(id) => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: `returns_toggle`,
                  responseName: id?.split(' ')?.[0],
                  ScreenName,
                  fundName: isin,
                  screenType: 'MF_L2'
                }
              })
              setCurrentToggleOption(() => id)
            }}
            toggleColor='#3E6CE3'
            toggleClassName='font-[500]'
          />
        </div>
      </div>
      <hr />
      <div className={`${peerData?.length > 0 ? 'pb-6' : 'pb-10'}  `}>
        {peerData?.length > 0 ? (
          <PeerList
            data={peerData}
            isinToBeHighlighted={alternateISIN}
            rank={getRank()}
            returnsText={`${currentToggleOption?.replace('Returns', '')} (p.a.)`}
            isRenderAllRows={false}
            isReadOnly={peersType === 'TRAILING'}
            onViewAll={openBottomSheet}
          />
        ) : (
          <PeerList
            data={Array(5)
              .fill(0)
              ?.map((_datum, idx) => {
                return {
                  rank: idx + 1,
                  name: idx === 2 ? 'Peer List Unavailable' : '-',
                  returns: '-',
                  isin: idx === 2 ? alternateISIN : ''
                }
              })}
            rank={3}
            isinToBeHighlighted={alternateISIN}
            returnsText={`${currentToggleOption?.replace('Returns', '')} (p.a.)`}
            isRenderAllRows={false}
            isError={true}
          />
        )}
        <PeersSheet
          header={
            <div className='flex flex-col text-left pb-2'>
              <div className={`text-black text-[14px] font-[500] pb-1 px-1`}>
                {peersType === 'ROLLING' ? 'Rolling' : 'Trailing'} Rank in {toPascalCaseWithSpaces(fundCategory)}
              </div>
              <div className={`flex gap-[4px] text-[#3D3D3D]  text-[14px] px-1`}>
                <div>Based on</div>
                <div>
                  <TextToggle
                    currentToggleOption={currentToggleOption}
                    toggleOptions={toggleOptions}
                    onToggleClick={(id) => setCurrentToggleOption(() => id)}
                    toggleColor='#3E6CE3'
                    toggleClassName='font-[500]'
                  />
                </div>
              </div>
            </div>
          }
          isOpen={isSheetOpen}
          onClose={closeBottomSheet}
        >
          <div className='w-full pl-4 pr-6 pb-1'>
            <PeerList
              data={peerData}
              rank={getRank()}
              isinToBeHighlighted={alternateISIN}
              returnsText={`${currentToggleOption?.replace('Returns', '')} (p.a.)`}
              isRenderAllRows={true}
              isReadOnly={peersType === 'TRAILING'}
            />
          </div>
        </PeersSheet>
      </div>
    </div>
  )
}
